import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "./Reveal";

const initialForm = { name: "", phone: "", email: "", package: "60-Minute Lesson — $80", preferred_date: "", message: "" };

const inputClass =
  "w-full bg-transparent border-b-2 border-white/20 focus:border-[#F5B301] outline-none py-4 text-lg text-[#f5f5f0] placeholder:text-white/30 transition-colors duration-300";

const contactRows = [
  [Phone, "0427 779 709 (text preferred)", "sms:0427779709"],
  [Mail, "reallifelearners@yahoo.com", "mailto:reallifelearners@yahoo.com"],
  [MapPin, "Coolum to Caloundra, Sunshine Coast QLD", null],
  [Clock, "Daytime lessons + Odd Hours 8PM–3AM", null],
];

export default function Booking() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — Cameron will be in touch within 24 hours.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" className="px-6 md:px-12 py-24 md:py-32 max-w-[1600px] mx-auto">
      <Reveal>
        <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#F5B301] mb-6">05 — Book Now</p>
        <h2 className="font-cabinet font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95] max-w-3xl text-[#f5f5f0]">
          Ready to take <span className="text-[#F5B301]">the first step?</span>
        </h2>
        <p className="font-script text-2xl md:text-3xl text-white/60 mt-6 -rotate-1">Text Cam, or send an enquiry below.</p>
      </Reveal>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 mt-16">
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="booking-name" className="text-xs font-bold uppercase tracking-widest text-white/50">Full Name</label>
                <input id="booking-name" data-testid="booking-name-input" required value={form.name} onChange={update("name")} placeholder="Alex Turner" className={inputClass} />
              </div>
              <div>
                <label htmlFor="booking-phone" className="text-xs font-bold uppercase tracking-widest text-white/50">Phone</label>
                <input id="booking-phone" data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={update("phone")} placeholder="0427 000 000" className={inputClass} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="booking-email" className="text-xs font-bold uppercase tracking-widest text-white/50">Email</label>
                <input id="booking-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="alex@email.com" className={inputClass} />
              </div>
              <div>
                <label htmlFor="booking-date" className="text-xs font-bold uppercase tracking-widest text-white/50">Preferred Start Date</label>
                <input id="booking-date" data-testid="booking-date-input" type="date" value={form.preferred_date} onChange={update("preferred_date")} className={`${inputClass} [color-scheme:dark]`} />
              </div>
            </div>
            <div>
              <label htmlFor="booking-package" className="text-xs font-bold uppercase tracking-widest text-white/50">Lesson Option</label>
              <select id="booking-package" data-testid="booking-package-select" value={form.package} onChange={update("package")} className={`${inputClass} cursor-pointer [&>option]:bg-[#111113]`}>
                <option>60-Minute Lesson — $80</option>
                <option>90-Minute Lesson — $110</option>
                <option>2-Hour Lesson — $150</option>
                <option>5 × 1HR Package — $375 (Save $25)</option>
                <option>Odd Hour Lesson (8PM–3AM)</option>
                <option>Not sure yet — advise me</option>
              </select>
            </div>
            <div>
              <label htmlFor="booking-message" className="text-xs font-bold uppercase tracking-widest text-white/50">Anything we should know?</label>
              <textarea id="booking-message" data-testid="booking-message-input" rows={3} value={form.message} onChange={update("message")} placeholder="Complete beginner, a bit nervous, prefer evenings…" className={`${inputClass} resize-none`} />
            </div>
            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={sending}
              className="group inline-flex items-center gap-3 bg-[#F5B301] text-[#0a0a0a] font-bold uppercase tracking-wide text-sm px-10 py-5 hover:bg-[#f5f5f0] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="bg-[#111113] border border-white/10 text-white p-10 md:p-12 h-full flex flex-col justify-between" data-testid="booking-contact-panel">
            <div>
              <h3 className="font-cabinet font-black uppercase tracking-tighter text-3xl">Let's work together.</h3>
              <p className="text-white/60 text-sm leading-relaxed mt-4">
                Every first Odd Hour Lesson includes a complimentary 30-minute parent &amp; learner chat — we'll put everyone at ease, answer questions, and build a plan around the learner.
              </p>
            </div>
            <div className="space-y-6 mt-12">
              {contactRows.map(([Icon, text, href]) => (
                <div key={text} className="flex items-center gap-4">
                  <span className="w-10 h-10 shrink-0 bg-[#F5B301] flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#0a0a0a]" />
                  </span>
                  {href ? (
                    <a href={href} className="text-sm font-semibold hover:text-[#F5B301] transition-colors duration-300">{text}</a>
                  ) : (
                    <span className="text-sm font-semibold">{text}</span>
                  )}
                </div>
              ))}
            </div>
            <p className="font-script text-2xl text-[#F5B301] mt-12 -rotate-1">
              Good drivers aren't born. They're taught, practised and caffeinated.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
